import type { QueryView } from '@krp/client-runtime'

import type { AqsiDeviceConnection, ConnectionVm } from '../../../shared/model/connection'
import type { CommandFeedback } from '../../../shared/model/feedback'
import type { SourceCaptionVm } from '../../source/model/types'

export interface AqsiMaterialization {
  configurationVersion: string
  completedAt: string
  count: number
}

/** Товар кабинета из базы службы (снимок C2). Цена — строкой: деньги не проходят через float. */
export interface AqsiGood {
  goodId: string
  name: string
  sku: string | null
  price: string | null
  barcodes: readonly string[]
  tax: number | null
  subject: number | null
  unit: string | null
  unitCode: number | null
  isWeight: boolean | null
  revision: number
  observedAt: string
  missingSince: string | null
  presence: string
  invalidReason: string | null
}

export interface AqsiGoodsPage {
  page: number
  pageSize: number
  total: number
  materialization: AqsiMaterialization | null
  items: readonly AqsiGood[]
}

export interface AqsiDevice {
  deviceId: number
  serialNumber: string
  model: string | null
  fiscalStorageNumber: string | null
  /** Магазин кабинета: по нему кассы собираются в дерево структуры; `null` — касса без магазина. */
  shopId: string | null
  shopName: string | null
  receiptTaxSystem: number | null
  taxSystemSource: string
  observedAt: string
  missingSince: string | null
  invalidReason: string | null
  /** Связь службы с аппаратом (FIX-C3): кабинет её не отдаёт, служба выводит сама. */
  lastContactAt: string | null
  connection: AqsiDeviceConnection | null
}

export interface AqsiDevices {
  materialization: AqsiMaterialization | null
  items: readonly AqsiDevice[]
}

export interface AqsiCashier {
  cashierId: string
  name: string
  observedAt: string
  missingSince: string | null
}

export interface AqsiCashiers {
  materialization: AqsiMaterialization | null
  items: readonly AqsiCashier[]
}

export interface AqsiRefreshResult {
  scope: string
  completedAt: string
  seen: number
  added: number
  changed: number
  newlyMissing: number
  invalid: number
  unkeyed: number
}

/** Состояние отдельного индекса поиска по снимку кабинета (RP-SE12). `snapshotAt` — по какому снимку построен, `latestSnapshotAt` — текущий снимок. */
export type AqsiProbeState = 'none' | 'building' | 'ready' | 'failed'

export interface AqsiProbeStatus {
  state: AqsiProbeState
  builtAt: string | null
  snapshotAt: string | null
  latestSnapshotAt: string | null
  count: number
  error: string | null
  pending: number
}

/** Чем закончилась последняя проверка кабинета (RP-SE13). */
export type AqsiFreshnessOutcome = 'never' | 'unchanged' | 'changed' | 'full' | 'failed'

/**
 * Сводка ставки НДС товаров кабинета по снимку в нашей базе. `available = false` — каталог ещё не читался: числа `null`
 * («нет данных»). Кабинет отдаёт ставку кодом без названия.
 */
export interface AqsiTaxSummary {
  available: boolean
  reason: string | null
  total: number | null
  withTax: number | null
  withoutTax: number | null
  byTax: { tax: number; count: number }[]
  snapshotAt: string | null
}

/** Свежесть каталога одного кабинета: сколько товаров, когда проверяли, чем кончилось, когда следующая проверка. */
export interface AqsiFreshness {
  products: number
  snapshotAt: string | null
  lastCheckedAt: string | null
  outcome: AqsiFreshnessOutcome
  fullReason: string | null
  added: number
  changed: number
  missing: number
  running: boolean
  nextCheckAt: string | null
  intervalMinutes: number
  online: boolean
  errorCode: string | null
  errorMessage: string | null
}

/** Ответ «Проверить актуальность»: `done` - проверено; `already-running` и `throttled` - запроса в кабинет не было. */
export type AqsiFreshnessRunStatus = 'done' | 'already-running' | 'throttled' | 'failed'

export interface AqsiFreshnessCheck {
  status: AqsiFreshnessRunStatus
  retryAfter: string | null
  freshness: AqsiFreshness
}

export interface AqsiFiscalBlock {
  code: string
  message: string
  goodId: string | null
}

export interface AqsiFiscalCheck {
  ready: boolean
  blocks: readonly AqsiFiscalBlock[]
  /** Сводка профиля: что служба проверила (полный профиль — для продажи C3b). */
  profile: { taxSystem: number; legalEntityInn: string; checkedAt: string; lines: number } | null
}

/** Намерение каталога по rid: страница и отметки для проверки профиля. Срок — `screen`. */
export interface CatalogRidIntent {
  page: number
  checkDeviceId: number | null
  checkGoodIds: readonly string[]
}

export interface CatalogIntentState {
  byResource: Record<string, CatalogRidIntent>
}

export const CATALOG_PAGE_SIZE = 50
/** Сколько товаров можно отметить для проверки — разумный предел запроса. */
export const MAX_CHECK_GOODS = 50

export interface GoodRowVm {
  goodId: string
  name: string
  sku: string
  price: string
  barcodes: string
  state: string
  stateKind: 'ok' | 'missing' | 'invalid'
  checked: boolean
}

export interface DeviceRowVm {
  deviceId: number
  serial: string
  model: string
  shop: string
  taxSystem: string
  state: string
  stateKind: 'ok' | 'missing' | 'invalid'
  connection: ConnectionVm | null
}

export interface CatalogVm {
  resourceId: string
  source: SourceCaptionVm
  goodsQuery: QueryView
  goods: readonly GoodRowVm[]
  page: number
  pageCount: number
  total: number
  goodsSnapshot: string
  devicesQuery: QueryView
  devices: readonly DeviceRowVm[]
  devicesSnapshot: string
  cashiersQuery: QueryView
  cashiers: readonly { id: string; name: string; state: string }[]
  check: {
    deviceId: number | null
    deviceOptions: readonly { value: string; label: string }[]
    goodCount: number
    canRun: boolean
    blockedReason: string | null
    result: { ready: boolean; title: string; lines: readonly string[] } | null
  }
  busy: string | null
  feedback: CommandFeedback | null
}

export interface CatalogActions {
  reload: () => void
  setPage: (page: number) => void
  refreshCatalog: () => void
  refreshDevices: () => void
  toggleGood: (goodId: string) => void
  setCheckDevice: (deviceId: number | null) => void
  runCheck: () => void
  dismissFeedback: () => void
  switchSource: () => void
  /** Открыть окно товара источника (правка карточки в самом кабинете). Нет — название товара просто текст. */
  openGood?: (goodId: string) => void
}
